import moment from 'moment'
import orderBy from 'lodash/orderBy'
import { DATE_FORM_FORMAT } from '@/constants/date'
import { numberWithSpaces } from '@/helpers/render-value'

export const GROUP_BY_VALUES = ['period', 'audience', 'advertiser', 'segment', 'cohort', 'partner', 'offer']
export const COHORT_VALUES = ['clicks_no_reg', 'reg_no_dep', 'depositors', 'unrecognised_token']
export const STATUS_VALUES = ['attributed', 'not_attributed', 'pending']

// Column order and display format of the report metrics (CPA-3455)
export const METRICS = [
  { value: 'clicks', type: 'int' },
  { value: 'unique', type: 'int' },
  { value: 'players', type: 'int' },
  { value: 'attributed', type: 'int' },
  { value: 'attribution_cr', type: 'percent' },
  { value: 'new_regs', type: 'int' },
  { value: 'new_deps', type: 'int' },
  { value: 'amount_deps', type: 'money' },
  { value: 'income', type: 'money' },
  { value: 'spend', type: 'money' },
  { value: 'cfd', type: 'money' },
  { value: 'roi', type: 'percent' },
  { value: 'average_check', type: 'money' },
  { value: 'pending', type: 'int' },
]
export const METRIC_TYPES = METRICS.reduce((acc, { value, type }) => ({ ...acc, [value]: type }), {})

// First and second group value columns of the table
export const GROUP_COL = 'g1'
export const SECOND_GROUP_COL = 'g2'

export const defaultFilters = () => ({
  filter: {
    dateFrom: moment().startOf('month').format(DATE_FORM_FORMAT),
    dateTo: moment().endOf('month').format(DATE_FORM_FORMAT),
    groupBy: 'period',
    secondGroupBy: '',
    audiences: [],
    advertisers: [],
    segments: [],
    cohorts: [],
    partners: [],
    offers: [],
    statuses: [...STATUS_VALUES],
  },
  sortBy: [GROUP_COL],
  sortDesc: [true],
  clientOptions: {
    filterPanelExpanded: true,
  },
})

/**
 * Cell text: null (zero denominator) is an empty cell, not 0.
 */
export function formatMetric(key, value) {
  if (value === null || value === undefined || value === '') return ''
  switch (METRIC_TYPES[key]) {
    case 'int':
      return numberWithSpaces(Math.round(value))
    case 'percent':
      return `${Number(value).toFixed(2)}%`
    case 'money':
      return numberWithSpaces(Number(value).toFixed(2))
    default:
      return value
  }
}

// Period keys (Y-m-d) sort as dates, the other groups by title, like the backend does
function groupSortValue(row) {
  if (typeof row.key === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(row.key)) return row.key
  return (row.title || '').toString().toLowerCase()
}

function sortLevel(rows, sortBy, sortDesc) {
  const order = sortDesc ? 'desc' : 'asc'
  if (!sortBy || [GROUP_COL, SECOND_GROUP_COL].includes(sortBy)) {
    return orderBy(rows, [groupSortValue], [order])
  }
  // Empty values (cFD without New deps etc.) always go to the end
  const withValue = rows.filter(row => row[sortBy] !== null && row[sortBy] !== undefined)
  const empty = rows.filter(row => row[sortBy] === null || row[sortBy] === undefined)
  return [...orderBy(withValue, [sortBy], [order]), ...empty]
}

/**
 * Sorts both levels with the same key, the way the backend sorts the report.
 */
export function sortReportRows(rows, { sortBy = [], sortDesc = [] } = {}) {
  const key = sortBy[0] || GROUP_COL
  const desc = sortBy.length ? !!sortDesc[0] : true
  return sortLevel(rows, key, desc).map(row => (row.sg ? { ...row, sg: sortLevel(row.sg, key, desc) } : row))
}
